import {
  Flex,
  Typography,
  SafeAreaTopSpacing,
  NaviIconBack,
  IconSearchCamera,
  IconSearch,
  IconCancel,
  ColorValues,
  useScreenSize,
} from '@banhala/ui-kit';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TextInput } from 'react-native';
import {
  useHandleBackPressed,
  useOpenDeeplink,
  useSafeTimeout,
} from 'shared/src/hooks';
import { bridge, isAndroid, isWeb } from 'shared/src/utils';
import { DESKTOP_MAX_WIDTH } from 'shared/src/constants/layout';
import { useConfigs } from '../queries/useConfigs';
import { SearchCameraTooltip } from './CameraTooltip/SearchCameraTooltip';
import { SearchType } from 'shared/src/api/enums';
import { useLogSearchKeyword } from '../hook/useLogSearchKeyword';
import { Accessibility } from '../../../utils/accessibility';

type PromotionPlaceholder = {
  text?: string;
  deeplink?: string;
};

type Props = {
  /** iOS store.placeholder (프로모션 placeholder) */
  placeholder?: PromotionPlaceholder | null;

  /** 최초 마운트 시 키보드 자동 포커스 (show_keyboard 1회 소비값) */
  autoFocusOnMount?: boolean;

  /** 이미지 검색 버튼 */
  onPressImageSearch?: () => void;

  /** query 변경 (iOS: queryStringDidChange) */
  onQueryChange?: (query: string) => void;

  /** 엔터/검색 실행 (iOS: performSearch) */
  onSubmitSearch?: (query: string) => void;

  /** clear 탭 (iOS: clearTextButtonDidTap) */
  onPressClear?: () => void;

  /** 초기 query 세팅 (검색결과→검색바 탭 케이스) */
  initialQuery?: string;

  /** SearchResult에서 복귀 시 포커스 트리거 (값이 변경될 때마다 포커스) */
  focusTrigger?: number;
};

const MAX_LENGTH = 20;

export const SearchHelperHeader: React.FC<Props> = ({
  autoFocusOnMount = false,
  onPressImageSearch,
  onQueryChange,
  onSubmitSearch,
  onPressClear,
  initialQuery = '',
  focusTrigger = 0,
}) => {
  const { onPressBack } = useHandleBackPressed();
  const inputRef = useRef<TextInput | null>(null);
  const { width: screenWidth } = useScreenSize();
  const contentWidth = Math.min(screenWidth, DESKTOP_MAX_WIDTH);

  const { data: configs } = useConfigs();
  const placeholder = configs?.search_placeholder ?? null;
  const logSearchKeyword = useLogSearchKeyword();
  const { openDeeplink } = useOpenDeeplink();

  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const setSafeTimeout = useSafeTimeout();

  const hasQuery = query.trim().length > 0;

  // 최초 마운트 시 autoFocusOnMount=true면 키보드 포커스 (페이지 레벨에서 1회 소비됨)
  // Android: RN 브릿지 + Activity attach + IME bind 완료 시점이 늦어 500ms 마진 필요.
  // (200ms로는 일부 기기/OS 에서 IMM.showSoftInput silently fail — stag 검증으로 500ms 확정)
  useEffect(() => {
    if (!autoFocusOnMount) return;
    setSafeTimeout(() => {
      inputRef.current?.focus();
    }, isAndroid ? 500 : 50);
  }, [autoFocusOnMount, setSafeTimeout]);

  // SearchResult에서 복귀 시 검색어 세팅 + 키보드 포커스
  useLayoutEffect(() => {
    if (focusTrigger === 0) return;
    setQuery(initialQuery);
    setSafeTimeout(() => inputRef.current?.focus(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrigger]);

  // 웹: navigation.navigate로 복귀 시 initialQuery prop 변경 감지
  const prevInitialQueryRef = useRef(initialQuery);
  useEffect(() => {
    if (prevInitialQueryRef.current === initialQuery) return;
    prevInitialQueryRef.current = initialQuery;
    if (initialQuery !== undefined) {
      setQuery(initialQuery);
      // 검색어가 있을 때만 포커스 (빈 문자열 = X버튼 초기화)
      if (initialQuery.length > 0) {
        setSafeTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [initialQuery, setSafeTimeout]);

  // textPublisher -> removeDuplicates -> queryStringDidChange
  useEffect(() => {
    if (!isFocused) return; // iOS도 firstResponder일 때만 store send
    onQueryChange?.(query);
  }, [query, isFocused, onQueryChange]);

  const showClear = hasQuery; // iOS alpha 애니메이션 대신 조건부 노출
  // updateSearchIconAtRight:
  // - 입력이 비어있을 때만 표시 (입력 시작하면 hidden)
  const showPromotionIconAtRight = !hasQuery;

  const handleChangeText = (text: string) => {
    // iOS shouldChangeCharactersIn: max 20
    const next = text.slice(0, MAX_LENGTH);
    setQuery(next);
  };

  const handlePressClear = () => {
    setQuery('');
    onPressClear?.();
    // iOS: clear 탭 후 다시 focus
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSubmitEditing = async () => {
    const keyword = query.trim();

    if (keyword.length === 0) {
      const deeplink = placeholder?.deeplink;
      if (deeplink) {
        openDeeplink(deeplink);
        return;
      }
      bridge.toast('검색어를 입력해주세요');
      return;
    }

    logSearchKeyword({
      searchType: SearchType.DIRECT,
      keyword,
      typedKeystroke: keyword,
    });

    onSubmitSearch?.(keyword);
  };

  const handlePressPromotion = async () => {
    const deeplink = placeholder?.deeplink;
    if (!deeplink) return;

    bridge.logAnalyticsEvent({
      name: 'CLICK_SEARCH_PROMOTION',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SCREEN: 'SEARCH',
        TITLE: placeholder?.text ?? '',
        DEEPLINK: deeplink,
      },
    });

    openDeeplink(deeplink);
  };

  return (
    // 웹: ui-kit List 와 동일한 자체 처리 패턴 (모바일 폭 가운데 정렬).
    // 모바일: undefined → 부모 폭 그대로 (Z폴드 fold/unfold 시 화면 폭 반영).
    <Flex
      backgroundColor="bg_primary"
      alignSelf={isWeb ? 'center' : undefined}
      maxWidth={isWeb ? contentWidth : undefined}
      width={isWeb ? '100%' : undefined}
    >
      <SafeAreaTopSpacing />
      <Flex
        height={48}
        padding={{ left: 2, right: 16 }}
        justifyContent="center"
      >
        <Flex.Row alignItems="center">
          <Flex
            testID={Accessibility.Common.backButton}
            width={44}
            height={48}
            justifyContent="center"
            alignItems="center"
            onPress={onPressBack}
          >
            <NaviIconBack width={24} height={24} fill={'black'} />
          </Flex>

          <Flex width={2} />

          <Flex
            testID={Accessibility.Common.searchField}
            flex={1}
            height={36}
            borderRadius={8}
            backgroundColor="gray20"
            justifyContent="center"
          >
            <Flex
              position="absolute"
              left={12}
              right={52}
              height={36}
              justifyContent="center"
            >
              <TextInput
                ref={r => {
                  inputRef.current = r;
                }}
                value={query}
                onChangeText={handleChangeText}
                maxLength={MAX_LENGTH}
                onFocus={() => {
                  setIsFocused(true);
                  // iOS editingDidBegin도 queryStringDidChange를 보냄
                  onQueryChange?.(query);
                }}
                multiline={false}
                onBlur={() => setIsFocused(false)}
                onSubmitEditing={handleSubmitEditing}
                placeholder={placeholder?.text ?? '하나만 사도 무료배송'}
                placeholderTextColor={ColorValues.gray60}
                returnKeyType="search"
                autoCorrect={false}
                autoCapitalize="none"
                spellCheck={false}
                style={{
                  padding: 0,
                  margin: 0,
                  height: 36,
                  includeFontPadding: false,
                  fontWeight: hasQuery ? 'normal' : '600',
                }}
              />
            </Flex>
            <Flex
              position="absolute"
              right={0}
              height={36}
              width={52}
              justifyContent="center"
              alignItems="center"
            >
              {showClear ? (
                <Flex
                  testID={Accessibility.Common.clearSearchKeywordButton}
                  width={36}
                  height={36}
                  justifyContent="center"
                  alignItems="flex-end"
                  onPress={handlePressClear}
                >
                  <IconCancel />
                </Flex>
              ) : (
                <>
                  {showPromotionIconAtRight && (
                    <Flex.Row alignItems="center" gap={8}>
                      <Flex width={1} height={16} backgroundColor="gray40" />
                      <Flex
                        width={20}
                        height={20}
                        justifyContent="center"
                        alignItems="center"
                        onPress={handlePressPromotion}
                      >
                        <IconSearch size={20} stroke="content_secondary" />
                      </Flex>
                    </Flex.Row>
                  )}
                </>
              )}
            </Flex>
          </Flex>

          {!isWeb && (
            <>
              <Flex width={12} />
              <Flex.Row
                testID={Accessibility.Search.imageSearchButton}
                alignItems="center"
                gap={4}
                onPress={onPressImageSearch}
              >
                <IconSearchCamera fill={'black'} />
                <Typography type="subtitle3" color="content_primary">
                  검색
                </Typography>
              </Flex.Row>
            </>
          )}
        </Flex.Row>
        {/* iOS: tooltip.top = searchBarView.bottom - 3, trailing = searchBarView.trailing.inset(12) */}
        {!isWeb && (
          <Flex position="absolute" top={45} right={12} zIndex={100}>
            <SearchCameraTooltip />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
